import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';

/**
 * Operator-only audit trail (GA Track B). Reads sf_admin_audit newest-first
 * — every mutating admin action (seat revoke, provider edit/re-sync, comp)
 * lands here. Server gates on isAdminEmail, so a non-admin just gets 403s.
 *
 * Offset-paginated; "Load more" appends the next page. See docs/ADMIN_TOOLS.md.
 */
type AuditRow = NonNullable<Awaited<ReturnType<typeof streamFlowApi.adminAuditLog>>['data']>[number];

const PAGE = 50;

export default function AdminAuditLog() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async (off: number, append: boolean) => {
    setLoading(true);
    const res = await streamFlowApi.adminAuditLog(PAGE, off);
    if (res.success && res.data) {
      setRows((prev) => (append ? [...prev, ...res.data!] : res.data!));
      setHasMore(res.data.length === PAGE);
      setOffset(off);
    } else if (res.error) {
      toast.error(res.error.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { void load(0, false); }, [load]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Admin Audit Log</h1>
          <p className="text-sm text-gray-500">Every operator action — who did what, when. Newest first.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => void load(0, false)} className="text-sm text-blue-600 hover:underline">Refresh</button>
          <button onClick={() => navigate('/admin/streaming')} className="text-sm text-blue-600 hover:underline">Live Streams ▸</button>
          <button onClick={() => navigate('/')} className="text-sm text-blue-600 hover:underline">← Dashboard</button>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">When</th>
              <th className="px-3 py-2 font-medium">Admin</th>
              <th className="px-3 py-2 font-medium">Action</th>
              <th className="px-3 py-2 font-medium">Target</th>
              <th className="px-3 py-2 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 align-top">
                <td className="px-3 py-2 whitespace-nowrap text-gray-600" title={r.created_at}>{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-3 py-2">{r.admin_email ?? '(unknown)'}</td>
                <td className="px-3 py-2"><span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-mono">{r.action}</span></td>
                <td className="px-3 py-2 text-gray-600">
                  {r.target_type ? <span className="text-xs">{r.target_type}</span> : null}
                  {r.target_id ? <span className="ml-1 font-mono text-[11px] text-gray-400">{r.target_id.slice(0, 8)}…</span> : null}
                </td>
                <td className="px-3 py-2">
                  {r.detail
                    ? <code className="text-[11px] text-gray-500 break-all">{JSON.stringify(r.detail)}</code>
                    : <span className="text-gray-300">—</span>}
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td className="px-3 py-8 text-center text-gray-500" colSpan={5}>No admin actions recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-center">
        {loading && <span className="text-sm text-gray-500">Loading…</span>}
        {!loading && hasMore && (
          <button
            onClick={() => void load(offset + PAGE, true)}
            className="rounded-md border border-gray-300 px-4 py-1.5 text-sm hover:bg-gray-50"
          >
            Load more
          </button>
        )}
      </div>
    </div>
  );
}
